import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import { Field } from 'react-final-form';
import { FlexColumn, FlexMiddle } from '@ubeya/shared-web/components';
import { ReactComponent as ChevronIcon } from '@ubeya/shared-web/assets/chevron-right.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

const ChannelCardWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
`;

const ChannelCardHeader = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  align-items: flex-start;
  transition: background 150ms;
  cursor: ${({ $isChecked }) => ($isChecked ? 'pointer' : 'default')};

  ${({ $isChecked, theme }) =>
    $isChecked &&
    css`
      &:hover {
        background: ${theme.colors.grey1};
      }
    `}
`;

const CheckboxLabel = styled.label`
  display: flex;
  flex: none;
  cursor: pointer;
`;

const ChannelExpanded = styled.div`
  padding: 8px 16px 16px 46px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChannelHeaderActions = styled(FlexMiddle)`
  gap: 10px;
`;

const ChannelTag = styled.span`
  border-radius: 16px;
  font-size: 11px;
  line-height: 16px;
  padding: 0 8px;
  background: ${({ $isChecked, $isEdited, theme }) => {
    if (!$isChecked) {
      return theme.colors.disabled;
    }

    return $isEdited ? theme.colors.primaryLight : theme.colors.grey2;
  }};
  color: ${({ $isChecked, theme }) => ($isChecked ? theme.colors.black : theme.colors.grey3)};
`;

const ChannelChevron = styled(ChevronIcon)`
  flex: none;
  width: 16px;
  height: 16px;
  transition: transform 150ms;
  transform: rotate(${({ $isOpen }) => ($isOpen ? -90 : 90)}deg);
`;

const CustomCheckbox = styled.div`
  position: relative;
  width: 18px;
  height: 18px;
  flex: none;
  margin-top: 1px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${({ $checked, theme }) => ($checked ? theme.colors.greenPositive : theme.colors.grey2)};
  background: ${({ $checked, theme }) => ($checked ? theme.colors.greenPositive : theme.colors.white)};
  transition: border-color 150ms, background 150ms, box-shadow 150ms;

  &:hover {
    border-color: ${({ $checked, theme }) => ($checked ? theme.colors.greenPositive : theme.colors.grey3)};
    box-shadow: ${({ $checked, theme }) => ($checked ? `0 0 0 3px ${theme.colors.greenPositive}26` : 'none')};
  }
`;

const CheckMark = styled.svg`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  color: ${({ theme }) => theme.colors.white};
`;

const ChannelContent = styled(FlexColumn)`
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

const ChannelLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const ChannelDescriptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ChannelDescription = styled.span`
  flex: none;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const ChannelValuePreview = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.black};
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const ChannelCheckboxField = ({
  name,
  label,
  description,
  fieldValue,
  isOpen,
  onToggleOpen,
  onCheckedChange,
  expandedContent
}) => {
  const { t } = useContext(ConfigContext);
  const isEdited = !!fieldValue;

  return (
    <Field
      name={name}
      render={({ input }) => {
        const isChecked = !!input.value;
        const isExpanded = isChecked && isOpen && !!expandedContent;

        const handleCheckedChange = () => {
          const nextChecked = !input.value;

          input.onChange(nextChecked);
          onCheckedChange(name, nextChecked);
        };

        return (
          <ChannelCardWrapper>
            <ChannelCardHeader $isChecked={isChecked} onClick={() => isChecked && onToggleOpen(name)}>
              <CheckboxLabel htmlFor={`channel-${name}`} onClick={(e) => e.stopPropagation()}>
                <HiddenInput
                  type="checkbox"
                  id={`channel-${name}`}
                  checked={isChecked}
                  onChange={handleCheckedChange}
                />
                <CustomCheckbox $checked={isChecked}>
                  <CheckMark $visible={isChecked} width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2.5 6.5l2.5 2.5 4.5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </CheckMark>
                </CustomCheckbox>
              </CheckboxLabel>
              <ChannelContent>
                <ChannelLabel>{label}</ChannelLabel>
                <ChannelDescriptionRow>
                  <ChannelDescription>{description}</ChannelDescription>
                  {!isExpanded && isEdited && <ChannelValuePreview>{`“${fieldValue}”`}</ChannelValuePreview>}
                </ChannelDescriptionRow>
              </ChannelContent>
              {!!expandedContent && (
                <ChannelHeaderActions>
                  <ChannelTag $isEdited={isEdited} $isChecked={isChecked}>
                    {t(isEdited ? 'publishTagEdited' : 'publishTagDefaultText')}
                  </ChannelTag>
                  <ChannelChevron $isOpen={isExpanded} />
                </ChannelHeaderActions>
              )}
            </ChannelCardHeader>
            {isExpanded && <ChannelExpanded>{expandedContent}</ChannelExpanded>}
          </ChannelCardWrapper>
        );
      }}
    />
  );
};

export default ChannelCheckboxField;
